import React from 'react'
import * as S from './mobile.styled'

import { Statuses } from '@app/courses/domain/ratingStatus'
import { getClubStatus } from '@app/courses/domain/ratingStatus2'
import { useSnackbar } from '@app/snackbar'
import { navigate } from '@app/navigation'
import { useApi } from '@common/hooks/useApi'
import { useAxios } from '@common/hooks/useAxios'

import { InLineSpinner } from '@app/evaluation/components/Spinner'
import { LIST_SPINNER_SIZE } from '@app/components/atoms/ListState'
import { LRClub } from '../../../types/Clubs'
import { MiProjectBranch, ProjectCourse } from '../../../types/Project'
import { MiProjectStatus } from '@app/evaluation-core/types'
import { RatingTemplate } from '../../../types/Templates'

import MobileCourseListItem from './MobileCourseListItem'

type Props = {
  club: LRClub
}

const MobileCourseList: React.FC<Props> = ({ club }) => {
  const axios = useAxios('/ratings/')
  const { data, isLoading } = useApi<MiProjectBranch>(
    `/courses/projects/${club.id}`
  )
  const [openSnackbar] = useSnackbar()

  const courses = data?.courses ?? []

  function isRateable(item: ProjectCourse) {
    return !item.tees.every((tee) => tee.playerCategory === '0')
  }

  async function createMappedRatingTemplate(course: ProjectCourse) {
    const holeIds = course.holes.map((hole) => hole.physicalID)
    const templateBody = {
      courseId: course.id,
      clubId: club.id,
      name: course.name,
      holeIds,
      defaultCourseSettings: {
        bunkDepthDefault: 0,
        grassType: 'cool',
        intermediateCut: 0,
        measurement: 'imperial',
        roughHeight: 3,
        stimpFeet: 9,
        stimpInches: 0,
        'wind_1-9': 0,
        'wind_10-18': 0,
      },
      mapped: true,
    }

    let templateId: string

    try {
      const { data } = await axios.get<RatingTemplate>(
        `/templates?holeIds=${holeIds.join(',')}`
      )
      templateId = data.id
    } catch {
      const { data } = await axios.post<RatingTemplate>(
        '/templates',
        templateBody
      )
      templateId = data.id
    }

    navigate('CreateRating', {
      holeIds,
      templateId,
      club,
      branchId: data?.projectBranchID,
      isRateable: isRateable(course),
    })
  }

  const checkClubStatus = (cb: () => void) => () => {
    const clubStatus = getClubStatus(club.branchStatus)
    if (clubStatus !== Statuses.READY_FOR_RATING) {
      return openSnackbar(
        'The club is not ready to rate. Please refer to LiveCR.'
      )
    }
    cb()
  }

  if (isLoading) {
    return (
      <S.Center>
        <InLineSpinner size={LIST_SPINNER_SIZE} />
      </S.Center>
    )
  }

  if (!data || data.courses.length <= 0) {
    return <S.Empty>No mapped courses exist for this golf club.</S.Empty>
  }

  return (
    <S.TableContainer>
      <S.CoursesList>
        {courses
          .slice()
          .sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { numeric: true })
          )
          .map((course) => (
            <MobileCourseListItem
              key={`course-${course.id}`}
              branchId={data.projectBranchID}
              courseRateable={isRateable(course)}
              hasActiveEval={false}
              projectCourse={course}
              clubId={club.id}
              lrCourse={club?.courses?.find((c) => c.id === course.id)}
              projectStatus={
                club.projectStatus &&
                (club.projectStatus.toString() as MiProjectStatus)
              }
              startEvaluation={checkClubStatus(() =>
                createMappedRatingTemplate(course)
              )}
              buttonText="Manage Rating"
            />
          ))}
      </S.CoursesList>
    </S.TableContainer>
  )
}

export default MobileCourseList
